import {computed, ref} from 'vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';
import {useJoinLinkShared} from '~/composables/useJoinLinkShared';
import {joinSummaryLine} from '~/utils/joinSettings';

// Reference types (InviteEntityReferenceType) — the one place the numbers live on the web.
export const JOIN_REF = Object.freeze({
    club: 0, ladder: 1, leagueCycle: 2, player: 3, tournament: 4, meet: 5, americanoCycle: 6,
});

export const joinEntityTypeName = (referenceType) => ({
    0: 'club', 1: 'ladder', 2: 'box league cycle', 4: 'tournament', 5: 'meet', 6: 'americano cycle',
}[referenceType] ?? 'entity');

/**
 * useJoinSettings — one entity's join settings (JOIN_SETTINGS_PLAN.md §5): the managed
 * join link plus the placement switch. `load()` mints the link when the entity has none
 * yet, so there is never a "create link" step. `save(patch)` merges onto the current
 * settings and PUTs the whole panel (the API wants the full request).
 */
export function useJoinSettings(referenceType, referenceId) {
    const {httpRequest} = useHttpRequest();
    const {markJoinLinkShared} = useJoinLinkShared();
    const settings = ref(null);
    const pending = ref(false);
    const saving = ref(false);
    const errors = ref(null);

    const type = () => (typeof referenceType === 'function' ? referenceType() : referenceType?.value ?? referenceType);
    const id = () => (typeof referenceId === 'function' ? referenceId() : referenceId?.value ?? referenceId);

    // Only the newest load may land. The dialog reloads when its entity changes,
    // and without this an older, slower response could arrive last and leave one
    // entity's link showing under another entity's name.
    let loadSeq = 0;
    const load = async () => {
        const seq = ++loadSeq;
        pending.value = true;
        errors.value = null;
        try {
            const result = await httpRequest(endpoints.joinSettings.get(type(), id()));
            if (seq === loadSeq) settings.value = result;
        } catch (e) {
            if (seq === loadSeq) errors.value = e;
        } finally {
            if (seq === loadSeq) pending.value = false;
        }
        return settings.value;
    };

    // The full PUT body from the current settings + a patch. A protected link stays
    // protected unless the caller says otherwise (passwordProtect true, no password,
    // existing hash = keep; see InviteLinkUpdateValidator).
    const buildRequest = (patch = {}) => {
        const link = settings.value?.link ?? {};
        return {
            enabled: settings.value?.enabled !== false,
            inviteScope: link.inviteScope ?? 0,
            inviteJoinSetting: link.inviteJoinSetting ?? 0,
            inviteEntityPageSetting: link.inviteEntityPageSetting ?? 2, // VisibleMembersOnly, the API's default for a new competition
            joinMessage: link.joinMessage ?? null,
            usageLimit: link.usageLimit ?? null,
            startsAt: link.startsAt ?? null,
            expiresAt: link.expiresAt ?? null,
            passwordProtect: !!link.isPasswordProtected,
            removePassword: false,
            ...patch,
        };
    };

    const save = async (patch = {}) => {
        saving.value = true;
        errors.value = null;
        try {
            settings.value = await httpRequest(endpoints.joinSettings.update(type(), id()), buildRequest(patch));
            return true;
        } catch (e) {
            errors.value = e;
            return false;
        } finally {
            saving.value = false;
        }
    };

    const regenerate = async () => {
        saving.value = true;
        errors.value = null;
        try {
            settings.value = await httpRequest(endpoints.joinSettings.regenerate(type(), id()));
            return true;
        } catch (e) {
            errors.value = e;
            return false;
        } finally {
            saving.value = false;
        }
    };

    const downloadPdf = async () => {
        const res = await httpRequest(endpoints.joinSettings.pdf(type(), id()));
        if (res?.url) window.open(res.url, '_blank');
    };

    const url = computed(() => settings.value?.url ?? '');
    // X141: a copy is a share; the setup guide's "Share an invite link" step ticks on it (reported, never awaited).
    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url.value);
            markJoinLinkShared(settings.value?.link?.slug);
            return true;
        } catch (e) {
            return false;
        }
    };

    // One line that says how joining behaves right now, in the Join settings dialog's own words (R43 L1: the card
    // and the dialog must agree). A club's link set to "club players" (2) works as link only there, as the
    // dialog reads it (JoinSettingsControls pageValue): club players are already in.
    const summary = computed(() => {
        const s = settings.value;
        if (!s) return '';
        if (s.enabled === false) return 'Nobody can join, joining is switched off';
        return joinSummaryLine(s);
    });

    // Link status the way the admin reads it.
    const status = computed(() => {
        if (settings.value && settings.value.enabled === false) return {key: 'off', label: 'Joining off'};
        const link = settings.value?.link;
        if (!link) return null;
        if (link.expiresAt && new Date(link.expiresAt) < new Date()) return {key: 'expired', label: 'Expired'};
        if (link.usageLimit && link.usedCount >= link.usageLimit) return {key: 'usedUp', label: 'Used up'};
        if (link.startsAt && new Date(link.startsAt) > new Date()) return {key: 'scheduled', label: 'Not yet active'};
        if (!link.isActive) return {key: 'inactive', label: 'Inactive'};
        return {key: 'active', label: 'Active'};
    });

    const entityTypeName = computed(() => settings.value?.entityTypeName ?? joinEntityTypeName(type()));

    return {settings, pending, saving, errors, load, save, regenerate, downloadPdf, url, copyUrl, status, summary, entityTypeName, buildRequest};
}
